import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { getClassNames, retrieveFromLocalStorage, adjustDateTimeZone } from '../../../../utils/util';

import SearchInput from '../../../common/inputs/search-inpt/SearchInput';
import Image from '../../../common/image/Image';
import Message from '../../../common/message/Message';

import { ReactComponent as ProfileIcon } from '../../../../resources/icons/profile.svg';
import { ReactComponent as SendIcon } from '../../../../resources/icons/send.svg';

import './direct-messages.scss';

const DirectMessages = ({ chats, activeChat, sendMessage }) => {
  const [searchString, setSearchString] = useState('');
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [filteredChats, setFilteredChats] = useState(chats);

  const endOfMessagesRef = useRef(null);
  const inputRef = useRef(null);
  const messageContainerRef = useRef(null);

  const navigate = useNavigate();

  const personExternalReference = retrieveFromLocalStorage('personExternalReference');

  useEffect(() => {
    if (!endOfMessagesRef.current) return;

    if (initialLoad) {
      endOfMessagesRef.current.scrollIntoView();
      setInitialLoad(false);
    } else {
      endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeChat?.messages?.length, initialLoad]);

  useEffect(() => {
    const filteredChats = chats.filter((chat) => {
      const { companyName, personName } = chat;

      const fieldsToFilterOn = `${companyName} ${personName}`.toLowerCase();

      return fieldsToFilterOn.includes(searchString.toLowerCase());
    });

    setFilteredChats(filteredChats);
  }, [searchString, chats]);

  const handleMessageChange = (event) => {
    setMessage(event.target.value);
    inputRef.current.style.height = '40px';
    inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;

    if (inputRef.current.scrollHeight <= 40) {
      messageContainerRef.current.style.height = 'calc(100vh - 370px)';
    } else if (inputRef.current.scrollHeight <= 60) {
      messageContainerRef.current.style.height = 'calc(100vh - 390px)';
      messageContainerRef.current.scrollBy(0, 40);
    } else if (inputRef.current.scrollHeight <= 82) {
      messageContainerRef.current.style.height = 'calc(100vh - 410px)';
      messageContainerRef.current.scrollBy(0, 40);
    }
  };

  const handleSendMessageClick = () => {
    if (!message || sendingMessage) return;

    setSendingMessage(true);

    sendMessage(message, () => {
      setMessage('');
      setSendingMessage(false);
      inputRef.current.style.height = '40px';
      messageContainerRef.current.style.height = 'calc(100vh - 370px)';
    });
  };

  const formatTime = (time) => {
    let date = adjustDateTimeZone('2023-01-01' + time);

    const hours = date.getHours();
    const minutes = date.getMinutes();

    return `${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
  };

  const renderRelativeTime = (date, time) => {
    const minuteInSeconds = 60;
    const hourInSeconds = minuteInSeconds * 60;
    const dayInSeconds = hourInSeconds * 24;
    const weekInSeconds = dayInSeconds * 7;
    const monthInSeconds = dayInSeconds * 30;

    const seconds = Math.floor(new Date() - new Date(date)) / 1000;

    let value = seconds / monthInSeconds;
    if (value > 1) {
      value = Math.floor(value);
      return `${value}M`;
    }

    value = seconds / weekInSeconds;
    if (value > 1) {
      value = Math.floor(value);
      return `${value}W`;
    }

    value = seconds / dayInSeconds;
    if (value > 1) {
      value = Math.floor(value);
      return `${value}D`;
    }

    return formatTime(time);
  };

  const renderChatList = () => {
    if (filteredChats.length === 0) {
      return (
        <li className="direct-messages__chat-list__no-chats">
          <p>No Chats</p>
        </li>
      );
    }

    return filteredChats.map((chat, index) => {
      return (
        <li
          key={index}
          className={getClassNames('direct-messages__chat-list__chat', {
            active: chat.personExternalReference === activeChat?.personExternalReference,
          })}
          onClick={() => navigate(`/market/direct-messages/${chat.chatExternalReference}`)}
        >
          <div>
            <p>{chat.companyName}</p>
            {chat.date && chat.time && <p>{renderRelativeTime(chat.date, chat.time)}</p>}
          </div>
          <p>{chat.personName}</p>
          <p>{chat.message}</p>
        </li>
      );
    });
  };

  const renderMessages = () => {
    if (activeChat.messages.length === 0) {
      return (
        <div className="direct-messages__chat__no-messages">
          <p>No Messages</p>
        </div>
      );
    }

    const reversedMessages = [...activeChat.messages].reverse();
    return reversedMessages.map((message, index) => {
      const right = message.personExternalReference === personExternalReference;

      return <Message key={index} message={message} right={right} />;
    });
  };

  return (
    <section className="direct-messages" data-testid="direct-messages">
      <section className="direct-messages__chat-list direct-messages__card" data-testid="chat-list">
        <section className="direct-messages__chat-list__search">
          <label htmlFor="search">Search</label>
          <SearchInput searchString={searchString} onChange={({ target }) => setSearchString(target.value)} />
        </section>
        <ul>{renderChatList()}</ul>
      </section>
      <section className="direct-messages__chat direct-messages__card" data-testid="chat">
        <p>Direct Messages</p>
        {activeChat && (
          <>
            <section className="direct-messages__chat__user">
              {activeChat.profilePicture ? (
                <Image externalReference={activeChat.profilePicture} />
              ) : (
                !activeChat.profilePicture && <ProfileIcon data-testid="profile-picture-placeholder" />
              )}
              <div>
                <p>{activeChat.companyName}</p>
                <p>{activeChat.personName}</p>
              </div>
            </section>
            <section className="direct-messages__chat__container direct-messages__card">
              <ul className="direct-messages__chat__messages" ref={messageContainerRef}>
                {renderMessages()}
                <span ref={endOfMessagesRef} />
              </ul>

              <div className="direct-messages__chat__input">
                <textarea value={message} onChange={handleMessageChange} ref={inputRef} data-testid="chat-input" />
                <SendIcon
                  className={getClassNames('direct-messages__chat__input__button', { sending: sendingMessage })}
                  onClick={handleSendMessageClick}
                  data-testid="send-icon"
                />
              </div>
            </section>
          </>
        )}
      </section>
    </section>
  );
};

DirectMessages.propTypes = {
  chats: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  activeChat: PropTypes.shape({}),
  sendMessage: PropTypes.func.isRequired,
};

DirectMessages.defaultProps = {
  activeChat: undefined,
};

export default DirectMessages;
